// Record identities on a live page: the key a repeated copy is selected,
// highlighted and acted on by, and the one text form a list of them travels in
// (a selection page variable, a `txn_action` step's `ids`).
//
// A single-column key is plain text; a COMPOSITE transaction id is the
// engine's JSON object (`{"ORDER_NO":"A1","LINE_NO":2}`), which contains commas.
// So a list is written comma-joined when every key is plain, and as a JSON
// array otherwise — and `parseIdList` reads both, plus one bare composite id.

/** Case-insensitive column read — data-source rows carry the engine's
 *  lowercased form keys while authors write metadata names (`FIRED_AT`). */
export function ciGet(row: Record<string, unknown> | null | undefined, col: string | undefined): unknown {
  if (!row || !col) return undefined
  if (Object.prototype.hasOwnProperty.call(row, col)) return row[col]
  const want = col.toLowerCase()
  for (const k of Object.keys(row)) if (k.toLowerCase() === want) return row[k]
  return undefined
}

/** A row's identity: the named column, else `_txn_id`, else `id`. '' when none. */
export function rowKey(row: Record<string, unknown>, keyColumn?: string): string {
  const v = keyColumn ? ciGet(row, keyColumn) : undefined
  const raw = v ?? row._txn_id ?? ciGet(row, 'id')
  if (raw == null) return ''
  return typeof raw === 'object' ? JSON.stringify(raw) : String(raw)
}

/** Read a list of ids from its text form (or an array). Never throws. */
export function parseIdList(value: unknown): string[] {
  if (value == null) return []
  if (Array.isArray(value)) return value.map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v))).filter(Boolean)
  const t = String(value).trim()
  if (!t) return []
  if (t.startsWith('[')) {
    try {
      const a = JSON.parse(t)
      if (Array.isArray(a)) return parseIdList(a)
    } catch { /* not JSON: fall through to the comma form */ }
  }
  // One composite id on its own (`{{line._txn_id}}` of a two-column key).
  if (t.startsWith('{')) return [t]
  return t.split(',').map((s) => s.trim()).filter(Boolean)
}

/** Write a list of ids in its text form (see the header). */
export function formatIdList(ids: string[]): string {
  const clean = ids.filter(Boolean)
  if (!clean.length) return ''
  return clean.some((k) => k.includes(',') || k.startsWith('[') || k.startsWith('{'))
    ? JSON.stringify(clean)
    : clean.join(',')
}

/** Toggle one key in a selection (single mode replaces, or clears on re-tap). */
export function toggleSelection(current: unknown, key: string, mode: 'single' | 'multi'): string {
  if (!key) return formatIdList(parseIdList(current))
  const ids = parseIdList(current)
  const has = ids.includes(key)
  if (mode === 'single') return has ? '' : formatIdList([key])
  return formatIdList(has ? ids.filter((k) => k !== key) : [...ids, key])
}

/** Drop selected keys whose rows are gone (a finished item left the source). */
export function pruneSelection(current: unknown, present: Set<string>): string {
  const ids = parseIdList(current)
  const kept = ids.filter((k) => present.has(k))
  return kept.length === ids.length ? (current == null ? '' : String(current)) : formatIdList(kept)
}
